import { Routes } from '@angular/router';
import { LayoutContainerComponent } from './shared/containers/layout-container.component';
import { AuthGuardService } from './shared/services/auth-guard.service';
import { AupGuardService } from './shared/services/aup-guard.service';
import { LabContextGuardService } from './shared/services/lab-context-guard.service';
import { GoogleCallbackComponent } from './views/authentication/containers/login/auth-container';

export const routes: Routes = [
  {
    path: 'auth/login',
    loadComponent: () =>
      import('./views/authentication/containers/login/login.container').then((m) => m.LoginContainerComponent),
    data: { breadcrumb: { alias: 'login', label: 'Login' } },
  },
  {
    path: 'callback',
    component: GoogleCallbackComponent,
  },
  {
    // Deliberately outside LayoutContainerComponent: the policy has to be
    // accepted before the app shell is usable, so there is nothing to show
    // behind it.
    path: 'acceptable-use',
    canActivate: [AuthGuardService],
    loadChildren: () =>
      import('./views/acceptable-use/acceptable-use.routing').then((routes) => routes.AcceptableUseRoutes),
    data: { breadcrumb: { alias: 'Acceptable Use Policy', label: 'Acceptable Use Policy' } },
  },
  {
    path: '',
    component: LayoutContainerComponent,
    canActivate: [AuthGuardService],
    canActivateChild: [AuthGuardService, AupGuardService, LabContextGuardService],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    children: [
      {
        path: 'select-lab',
        loadComponent: () =>
          import('./views/lab-selection/lab-selection.component').then((m) => m.LabSelectionComponent),
        data: { breadcrumb: { alias: 'Select Lab', label: 'Select Lab' } },
      },
      {
        path: 'dashboard',
        loadChildren: () => import('./views/dashboard/dashboard.routing').then((routes) => routes.DashboardRoutes),
        data: { breadcrumb: { alias: 'Dashboard', label: 'Dashboard' } },
      },
      {
        path: 'labs',
        loadChildren: () => import('./views/labs/lab.routing').then((routes) => routes.LabsRoutes),
        data: { breadcrumb: { alias: 'Labs', label: 'Labs' } },
      },
      {
        path: 'projects',
        loadChildren: () => import('./views/projects/project.routing').then((routes) => routes.ProjectsRoutes),
        data: { breadcrumb: { alias: 'Projects', label: 'Projects' } },
      },
      {
        path: 'user-management',
        loadChildren: () =>
          import('./views/user-management/user-mgmt.routing').then((routes) => routes.UserManagementRoutes),
        data: { breadcrumb: { alias: 'Administration / User Management', label: 'Administration / User Management' } },
      },
      {
        path: 'notifications',
        loadChildren: () =>
          import('./views/notifications/notifications.routing').then((routes) => routes.NotificationsRoutes),
        data: { breadcrumb: { alias: 'Notifications', label: 'Notifications' } },
      },
      {
        path: 'system-alerts',
        loadChildren: () =>
          import('./views/system-alerts/system-alerts.routing').then((routes) => routes.SystemAlertsRoutes),
        data: { breadcrumb: { alias: 'System Alerts', label: 'System Alerts' } },
      },
      {
        path: 'metadata-tags',
        loadChildren: () =>
          import('./views/metadata-tags/metadata-tags.routing').then((routes) => routes.MetadataTagsRoutes),
        data: { breadcrumb: { alias: 'Metadata Tags', label: 'Metadata Tags' } },
      },
      {
        path: 'datasets',
        loadChildren: () => import('./views/datasets/datasets.routing').then((routes) => routes.DatasetsRoutes),
        data: { breadcrumb: { alias: 'Data Catalog', label: 'Data Catalog' } },
      },
      {
        path: 'domain-allowlist',
        loadChildren: () =>
          import('./views/domain-allowlist/domain-allowlist.routing').then((routes) => routes.DomainAllowlistRoutes),
        data: {
          breadcrumb: { alias: 'Administration / Domain Allowlist', label: 'Administration / Domain Allowlist' },
        },
      },
      {
        path: 'aup-report',
        loadChildren: () => import('./views/aup-report/aup-report.routing').then((routes) => routes.AupReportRoutes),
        data: {
          breadcrumb: { alias: 'Administration / AUP Report', label: 'Administration / AUP Report' },
        },
      },
      {
        path: 'reminder-settings',
        loadChildren: () =>
          import('./views/reminder-settings/reminder-settings.routing').then((routes) => routes.ReminderSettingsRoutes),
        data: {
          breadcrumb: { alias: 'Administration / Reminder Settings', label: 'Administration / Reminder Settings' },
        },
      },
      {
        path: 'metadata-mgmt',
        loadChildren: () =>
          import('./views/metadata-mgmt/metadata-mgmt.routing').then((routes) => routes.MetadataMgmtRoutes),
        data: {
          breadcrumb: { alias: 'Administration / Metadata Management', label: 'Administration / Metadata Management' },
        },
      },
      {
        path: 'reporting',
        loadChildren: () => import('./views/reporting/reporting.routing').then((routes) => routes.ReportingRoutes),
        data: { breadcrumb: { alias: 'Reporting', label: 'Data Explorer' } },
      },
      {
        path: 'catalog-overview',
        loadChildren: () =>
          import('./views/catalog-overview/catalog-overview.routing').then((routes) => routes.CatalogOverviewRoutes),
        data: { breadcrumb: { alias: 'Catalog Overview', label: 'Catalog Overview' } },
      },
      {
        path: 'pipelines',
        loadChildren: () => import('./views/pipelines/pipelines.routing').then((routes) => routes.PipelinesRoutes),
        data: { breadcrumb: { alias: 'Pipelines', label: 'Pipelines' } },
      },
      {
        path: 'metadata-requests',
        loadChildren: () =>
          import('./views/metadata-option-requests/metadata-option-requests.routing').then(
            (routes) => routes.MetadataOptionRequestsRoutes,
          ),
        data: {
          breadcrumb: {
            alias: 'Administration / Metadata Requests',
            label: 'Administration / Metadata Requests',
          },
        },
      },
      {
        path: 'metadata-corrections',
        loadChildren: () =>
          import('./views/metadata-corrections/metadata-corrections.routing').then(
            (routes) => routes.MetadataCorrectionsRoutes,
          ),
        data: {
          breadcrumb: {
            alias: 'Administration / Metadata Corrections',
            label: 'Administration / Metadata Corrections',
          },
        },
      },
      {
        path: 'my-metadata-requests',
        canActivate: [AuthGuardService],
        loadComponent: () =>
          import('./views/metadata-option-requests/my-metadata-option-requests.component').then(
            (m) => m.MyMetadataOptionRequestsComponent,
          ),
        data: { breadcrumb: { alias: 'My Metadata Requests', label: 'My Metadata Requests' } },
      },
      {
        path: 'pathogens',
        loadChildren: () =>
          import('./views/pathogens/pathogens.routing').then(
            (routes) => routes.PathogensRoutes
          ),
        data: {
          breadcrumb: {
            alias: 'Administration / Pathogens',
            label: 'Administration / Pathogens',
          },
        },
      },
      {
        path: 'organization-management',
        loadChildren: () =>
          import('./views/organization-management/organization-management.routing').then(
            (routes) => routes.OrganizationManagementRoutes
          ),
        data: {
          breadcrumb: {
            alias: 'Administration / Organization Management',
            label: 'Administration / Organization Management',
          },
        },
      },
      {
        path: 'validation-rules',
        loadChildren: () =>
          import('./views/validation-rules/validation-rules.routing').then(
            (routes) => routes.ValidationRulesRoutes
          ),
        data: {
          breadcrumb: {
            alias: 'Administration / Validation Rules',
            label: 'Administration / Validation Rules',
          },
        },
      },
      {
        path: 'labs-projects',
        loadChildren: () =>
          import('./views/labs-projects-admin/labs-projects-admin.routing').then(
            (routes) => routes.LabsProjectsAdminRoutes
          ),
        data: {
          breadcrumb: {
            alias: 'Administration / Labs & Projects',
            label: 'Administration / Labs & Projects',
          },
        },
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: 'dashboard' },
];
